import React, { createContext, useState, useContext, useEffect } from "react";
import { propertyTypeDetailsList } from "utils/searchFilter";
import { locations } from "utils/constants";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

export const SearchContext = createContext();

export const SearchProvider = ({ children }) => {
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(null);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedPropertyType, setSelectedPropertyType] = useState(
    "Case - Appartamenti"
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigation = useNavigate();
  const location = useLocation();

  // House type checkboxes
  const [propertyTypeDetails, setPropertyTypeDetails] = useState(
    propertyTypeDetailsList.map((item) => ({
      ...item,
      checked: item.id === "TutteLeTipologie",
    }))
  );

  const openSearchDropdown = () => {
    setShowSearchDropdown(true);
  };

  const closeSearchDropdown = () => {
    setShowSearchDropdown(false);
  };

  const toggleFilterDropdown = (id) => {
    setIsFilterDropdownOpen(isFilterDropdownOpen === id ? null : id);
  };

  // search input change
  const handleSearchChange = async (e) => {
    const searchTerm = e.target.value.toLowerCase();
    setSearchTerm(searchTerm);

    setSearchLoading(true);

    const delay = 1000;

    try {
      const filteredResults = locations.filter(
        (location) =>
          (location.zip.includes(searchTerm) ||
            location.name.toLowerCase().includes(searchTerm)) &&
          !selectedLocations.some(
            (selectedLocation) => selectedLocation.zip === location.zip // Exclude selected locations
          )
      );

      setSearchResults(filteredResults);
    } catch (error) {
      setTimeout(() => {
        setSearchLoading(false);
      }, delay);
    } finally {
      setTimeout(() => {
        setSearchLoading(false);
      }, delay);
    }
  };

  // handle selected search from the dropdown
  const handleSelectLocation = (selectedLocationData) => {
    setSearchTerm("");

    // Check if the selected location is already in selectedLocations
    const isAlreadySelected = selectedLocations.some(
      (location) => location.zip === selectedLocationData.zip // Compare by zip code
    );

    if (!isAlreadySelected) {
      // Update the selectedLocations by appending the new location
      setSelectedLocations((prevLocations) => [
        ...prevLocations,
        selectedLocationData,
      ]);

      setShowSearchDropdown(false);
      setSearchResults([]);

      if (location.pathname !== "/") {
        // Collect all location ids, ensuring only those with zip codes are included
        const locationIds = [
          ...new Set([
            ...selectedLocations.filter((loc) => loc.zip).map((loc) => loc.id),
            ...(selectedLocationData.zip ? [selectedLocationData.id] : []),
          ]),
        ];

        // Collect zip codes, ensuring only valid ones are included
        const zipCodes = [
          ...new Set([
            ...selectedLocations.flatMap((loc) =>
              loc.zip ? loc.zip.split(",").map((zip) => zip.trim()) : []
            ),
            ...(selectedLocationData.zip
              ? selectedLocationData.zip.split(",").map((zip) => zip.trim())
              : []),
          ]),
        ];

        // Clear previous query parameters
        searchParams.delete("locationId");
        searchParams.delete("zip");

        // Append all location ids as query parameters
        locationIds.forEach((id) => {
          searchParams.append("locationId", id); // Append location id
        });

        // Append all zip codes as query parameters
        zipCodes.forEach((zip) => {
          searchParams.append("zip", zip); // Append zip codes
        });

        const newPath = `/research`; // Path remains the same
        navigation(`${newPath}?${searchParams.toString()}`);
      }
    }
  };

  const handleRemoveLocation = () => {
    setSelectedLocations([]);
  };

  return (
    <SearchContext.Provider
      value={{
        isFilterDropdownOpen,
        showSearchDropdown,
        selectedLocations,
        selectedPropertyType,
        setSelectedPropertyType,
        searchTerm,
        searchResults,
        handleSelectLocation,
        handleSearchChange,
        setSelectedLocations,
        openSearchDropdown,
        closeSearchDropdown,
        toggleFilterDropdown,
        handleRemoveLocation,
        searchLoading,
        propertyTypeDetails,
        setPropertyTypeDetails,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
